import React, { useEffect, useRef, useState } from "react";
import {
  Spin,
  Select,
  Switch,
  Button,
  Table,
  InputNumber,
  Divider,
  Popconfirm,
  Tooltip,
  Alert,
} from "antd";
import { faEthernet, faListCheck } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
// COMPONENTS
import FormInputText from "../../components/form/FormInputText";
import DrawerRowDivider from "../../components/drawer/DrawerRowDivider";
import { describeError } from "../utils/describeError";
import GlobalAlert2 from "../../home/GlobalAlert2";
import SelectLn2Message from "../../components/select/SelectLn2Message";
// REDUX
import {
  usePostChannelMutation,
  usePutChannelMutation,
  usePatchChannelActiveMutation,
  useLazyGetChannelsByServerQuery,
  useLazyGetChannelQuery,
  useDeleteChannelMutation,
} from "../../../redux/channels/channelAPI";
import { useLazyGetBanksByServerQuery, useDeleteBankMutation } from "../../../redux/banks/bankAPI";
// AUTH
import GETJwtToken from "../../../redux/authentication/authentication";
// CONTEXT
import { useWizard } from "../WizardContext";
// STORE
import { RootState } from "../../../app/store";
// ENTITY LINK
import WizardEntityLink from "../WizardEntityLink";
import WizardReadOnlyId from "../WizardReadOnlyId";
import WizardExportButton from "../components/WizardExportButton";
import { WizardOpGate, useCurrentWizardStepAccess } from "../WizardStepGate";
// FLATBUFFER HELPERS
import { formatUuidT } from "../../../flatbuffers/display";
import { ChannelMode } from "../../../flatbuffers/generated/archive/channel-mode";
import { ChannelType } from "../../../flatbuffers/generated/archive/channel-type";
import EndpointTable, { EndpointRow, validateEndpoints } from "../components/EndpointTable";
import ControlRouteTable, {
  ControlRouteRow,
  RoutableEndpoint,
} from "../components/ControlRouteTable";
import { ControlWriteMode } from "../../../flatbuffers/generated/archive/control-write-mode";
import {
  diffControlRoutes,
  toControlRouteRows,
  toRoutableEndpoints,
} from "./controlRoutePersistence";
import {
  useLazyGetControlRoutesQuery,
  usePostControlRouteMutation,
  usePutControlRouteMutation,
  useDeleteControlRouteMutation,
} from "../../../redux/controlRoutes/controlRouteAPI";
import { useLazyGetChannelEndpointsQuery } from "../../../redux/channelEndpoints/channelEndpointAPI";
import ChannelMtlsEditor from "../components/ChannelMtlsEditor";
import ChannelSessionEditor, {
  type ChannelSessionState,
  newChannelSession,
  sessionApplies,
  sessionConfigFrom,
  sessionProblem,
  sessionRequestFields,
  sessionStateFromChannel,
} from "../components/ChannelSessionEditor";
import { SessionConfigT } from "../../../flatbuffers/generated/session/session-config";
import { EncodedMessageContent } from "../../../flatbuffers/generated/ln2/encoded-message-content";
import {
  compositeKeyToMessageT,
  messageTToCompositeKey,
} from "../../../flatbuffers/types/valuedMessageBuilder";
// SMART INVALIDATION
import {
  CHANNEL_FIELD_RULES,
  computeInvalidatedSteps,
  WizardStep,
  wizardStepsFrom,
} from "../utils/wizardInvalidation";
import { useWizardStepDraft } from "../utils/useWizardStepDraft";
// INTERFACES
import { ChannelInterface } from "../../../interfaces/Channel.interface";
import { ChannelConnectionType } from "../../../types/enums";
import { FormularyType } from "../../../interfaces/Wizard.interface";
import {
  CHANNEL_TYPE_SINGLE,
  CHANNEL_TYPE_ROUTABLE,
  channelTypeFromRoutable,
  routableFromChannelType,
} from "../../../constants/channelRouting";
import {
  CHANNEL_CONNECTION_TYPE_TO_FBS,
  CHANNEL_TYPE_NAMES,
  channelHasValidAddress,
  isValidOpcUaEndpointUrl,
  resolveChannelOpcUaEndpointUrl,
  resolveChannelSocketAddress,
  validateSocketAddress,
} from "../utils/channelAddress";

/** Map app-level ChannelConnectionType to the generated FlatBuffer enum. */
function toFbsChannelType(ct: ChannelConnectionType): ChannelType {
  return CHANNEL_CONNECTION_TYPE_TO_FBS[ct];
}

const TRANSPORT_MODES: ChannelConnectionType[] = [
  ChannelConnectionType.Tcp,
  ChannelConnectionType.Udp,
  ChannelConnectionType.Serial,
  ChannelConnectionType.Adapter,
];

/**
 * What the Transport Mode dropdown shows for each transport. ADAPTER is the
 * one whose name says nothing to an operator on its own: it is the socketless
 * channel an OPC UA adapter (addressed by the OPC UA Endpoint URL below) or a
 * Numato relay controller board sits on.
 */
const TRANSPORT_MODE_LABELS: Record<ChannelConnectionType, string> = {
  [ChannelConnectionType.Tcp]: ChannelConnectionType.Tcp,
  [ChannelConnectionType.Udp]: ChannelConnectionType.Udp,
  [ChannelConnectionType.Serial]: ChannelConnectionType.Serial,
  [ChannelConnectionType.Adapter]: "ADAPTER (OPC UA endpoint / controller board)",
};

/**
 * The transport a stored channel uses, read from its connection type. The
 * FlatBuffer response carries the generated numeric enum and a legacy JSON one
 * the name; `String(1)` is not a transport name, which is how every edited
 * channel used to reopen as TCP. `type` may be a non-transport classification
 * on older rows, so it is not consulted.
 */
export function transportFromConnectionType(raw: unknown): ChannelConnectionType {
  if (typeof raw === "number") return CHANNEL_TYPE_NAMES[raw] ?? ChannelConnectionType.Tcp;
  if (typeof raw === "string") {
    const upper = raw.toUpperCase();
    return TRANSPORT_MODES.find((mode) => mode === upper) ?? ChannelConnectionType.Tcp;
  }
  return ChannelConnectionType.Tcp;
}

/**
 * The intro line above the channel form. It states what the server really
 * has: nothing creates a channel alongside a server (the Server step's POST
 * sends none, and LiftCloud builds only the channels it is sent), so the old
 * fixed "A default channel was created with your server" was false for every
 * new server.
 */
export function channelsIntroMessage(channelCount: number): string {
  const adapters = "Adapters are assigned at the bank level (next step).";
  if (channelCount === 0) {
    return `This server has no channels yet. Add the channel VTS uses to reach the controller below. ${adapters}`;
  }
  const noun = channelCount === 1 ? "channel" : "channels";
  return `This server has ${channelCount} ${noun}. Edit one from the table above, or add more below. ${adapters}`;
}

const CHANNEL_MODE_OPTIONS: { label: string; value: ChannelMode }[] = [
  { label: "SINGLE", value: ChannelMode.SINGLE },
  { label: "FAILOVER", value: ChannelMode.FAILOVER },
  { label: "MERGE", value: ChannelMode.MERGE },
  { label: "MIXED", value: ChannelMode.MIXED },
  // LOAD_BALANCE reserved — not offered in v1
];

const HEARTBEAT_ENCODING_OPTIONS: { label: string; value: EncodedMessageContent }[] = [
  { label: "OneHot", value: EncodedMessageContent.OneHotMessage },
  { label: "BitMask", value: EncodedMessageContent.BitMaskMessage },
];

/**
 * Normalize a heartbeat frequency loaded from the server into the form's
 * `number | null` state. `heartbeat_frequency` is a FlatBuffer scalar that
 * defaults to 0 when unset, so a channel saved with no heartbeat round-trips
 * back as 0 rather than null. The valid range is 100–60000 ms, so any
 * non-positive value means "not set" and must become null — otherwise the
 * InputNumber renders a spurious 0 on reload that the operator has to backspace
 * away to clear the field.
 */
export const normalizeHeartbeatFrequency = (raw: number | null | undefined): number | null =>
  typeof raw === "number" && raw > 0 ? raw : null;

export interface ChannelRow {
  key: string;
  id: string;
  name: string;
  type: ChannelConnectionType;
  active: boolean;
  routable: boolean;
  socketAddress: string;
  /**
   * The channel's OPC UA endpoint as loaded from the server; `""` when it has
   * none. Held on the row rather than only in the edit form because a
   * `opc.tcp://host:port` endpoint is itself valid addressing for a TCP
   * channel (see `isChannelAddressingValid`), so the step-completeness check
   * has to be able to see it — and because an operator told a channel is
   * unaddressed cannot otherwise tell which channel has an endpoint at all.
   */
  opcUaEndpointUrl: string;
  heartbeatFrequency: number | null;
  mtlsEnabled: boolean;
  mtlsSecretName: string;
}

/**
 * Whether a channel's addressing is complete enough to save.
 *
 * An `opc.tcp://host:port` endpoint is itself the vendor address: LiftCloud's
 * `resolveSockets` short-circuits OPC UA to `resolveOpcUaSocket`, which reads
 * only `Channel.OpcUaEndpointURL` and never looks at `vendor_socket_address`.
 * Requiring the TCP socket address as well made the operator type the same
 * host:port twice, so a valid endpoint excuses an *absent* TCP socket
 * address. It does not excuse a *malformed* one: a typed-but-invalid socket
 * address is a value that would otherwise be persisted as-is, and the
 * endpoint being fine says nothing about whether that typed value is usable.
 *
 * UDP is unchanged: it carries a controller address and an edge address that
 * are genuinely two different endpoints, and an OPC UA URL replaces neither.
 */
export function isChannelAddressingValid(args: {
  isTcp: boolean;
  isUdp: boolean;
  socketAddressTyped: boolean;
  socketAddressIsValid: boolean;
  vendorAddressIsValid: boolean;
  opcUaEndpointSatisfiesAddress: boolean;
}): boolean {
  const {
    isTcp,
    isUdp,
    socketAddressTyped,
    socketAddressIsValid,
    vendorAddressIsValid,
    opcUaEndpointSatisfiesAddress,
  } = args;
  const tcpAddressValid =
    !isTcp || socketAddressIsValid || (!socketAddressTyped && opcUaEndpointSatisfiesAddress);
  const udpAddressesValid = !isUdp || (vendorAddressIsValid && socketAddressIsValid);
  return tcpAddressValid && udpAddressesValid;
}

// The pure addressing rules live in utils/channelAddress so the resume path in
// wizardCompletion applies the same predicate; re-exported for existing importers.
export {
  isValidOpcUaEndpointUrl,
  resolveChannelOpcUaEndpointUrl,
  resolveChannelSocketAddress,
  validateSocketAddress,
};
export type { OpcUaEndpointCarrier } from "../utils/channelAddress";

/** All channels must have valid socket addresses for the step to be complete. */
export function allChannelsValid(rows: ChannelRow[]): boolean {
  if (rows.length === 0) return false;
  return rows.every(channelHasValidAddress);
}

export interface EditAddresses {
  socketAddress: string;
  vendorSocketAddress: string;
  serialPath: string;
}

export type SocketAddressFields = Pick<
  ChannelInterface,
  "routableSocketAddress" | "vendorSocketAddress"
>;

/**
 * Derives form field values from an API channel when the user clicks Edit.
 * Handles all three transport modes and falls back to vendor_socket_address
 * when routable_socket_address is null (LN1, Modbus, and other non-CGA channels).
 */
export function resolveEditAddresses(
  ch: Pick<ChannelInterface, "routableSocketAddress" | "vendorSocketAddress">,
  transport: ChannelConnectionType,
): EditAddresses {
  if (transport === ChannelConnectionType.Udp) {
    if (ch.vendorSocketAddress) {
      return {
        vendorSocketAddress: String(ch.vendorSocketAddress),
        socketAddress: String(ch.routableSocketAddress || ""),
        serialPath: "",
      };
    }
    if (ch.routableSocketAddress) {
      const parts = String(ch.routableSocketAddress).match(/^([^:]+:\d+):([^:]+:\d+)$/);
      if (parts) {
        return {
          vendorSocketAddress: parts[1],
          socketAddress: parts[2],
          serialPath: "",
        };
      }
      return {
        vendorSocketAddress: String(ch.routableSocketAddress),
        socketAddress: "",
        serialPath: "",
      };
    }
    return { socketAddress: "", vendorSocketAddress: "", serialPath: "" };
  }

  if (transport === ChannelConnectionType.Serial) {
    return {
      serialPath: resolveChannelSocketAddress(ch),
      socketAddress: "",
      vendorSocketAddress: "",
    };
  }

  // TCP
  return {
    socketAddress: resolveChannelSocketAddress(ch),
    vendorSocketAddress: "",
    serialPath: "",
  };
}

/**
 * The normalized field set the channel create/update body is built from,
 * populated from the create/edit form's component state
 * (`handleAddChannel`/`handleUpdateChannel` via `channelFormFields`).
 * `buildChannelBody` is the single place that turns it into the API request
 * body. The quick enable/disable toggle does NOT go through this — it PATCHes
 * just `active` via the dedicated true-partial-update endpoint instead (see
 * `toggleChannelActive`).
 */
export interface ChannelBodyFields {
  name: string;
  transport: ChannelConnectionType;
  active: boolean;
  routable: boolean;
  heartbeatFrequency: number | null;
  heartbeatMessageId: string;
  heartbeatEncoding: EncodedMessageContent;
  socketAddress: string;
  vendorSocketAddress: string;
  serialPath: string;
  baudRate: number;
  channelMode: ChannelMode;
  controlWriteMode: ControlWriteMode;
  endpoints: EndpointRow[];
  opcUaEndpointUrl: string;
  mtlsEnabled: boolean;
  mtlsSecretName: string;
  /**
   * The session section. Absent, the body carries no session key at all, which
   * LiftCloud reads as "keep the stored session and its secrets".
   */
  session?: ChannelSessionState;
}

/**
 * Builds the full create/update body for a channel from a normalized
 * `ChannelBodyFields`. LiftCloud's channel POST/PUT endpoints are not true
 * partial updates — `buildChannelUpdateMap` (channel.controller.go)
 * unconditionally writes name/type/connectionType/addresses/heartbeat from
 * whatever the client sends, silently zeroing or nulling anything omitted —
 * so every field below must always be included. The single shared function
 * (rather than one copy per caller) means a future field addition only
 * needs one edit instead of three.
 */
export function buildChannelBody(
  id: string,
  serverId: string | null,
  organizationId: string | null,
  fields: ChannelBodyFields,
): Record<string, unknown> {
  const fbsType = toFbsChannelType(fields.transport);
  const body: Record<string, unknown> = {
    id,
    name: fields.name,
    type: fbsType,
    connectionType: fbsType,
    active: fields.active,
    routable: fields.routable,
    heartbeatFrequency: fields.heartbeatFrequency,
    serverId,
    organizationId,
  };
  // Transport mode and channel type are orthogonal. The transport decides where
  // the address comes from (and any transport-only extras like baudRate); the
  // channel type decides which column it lands in. A ROUTABLE channel addresses
  // its controller through routable_socket_address — one socket dispatching to
  // many banks via VTS's CGA / LobbyVision / LobbyVision2 accumulation map —
  // while a SINGLE channel uses vendor_socket_address for the direct path. Any
  // transport can be either type, so the two must not be folded together:
  // keying the column off the transport is what put routed LobbyVision2 TCP
  // channels in the vendor column, leaving the routed slot empty.
  if (fields.transport === ChannelConnectionType.Udp) {
    // UDP is the one transport with two genuinely distinct addresses: the
    // Controller IP:Port is always the vendor-side address, and a routed UDP
    // channel (CGA) carries the dispatch address in addition to it, not
    // instead of it.
    body.vendorSocketAddress = fields.vendorSocketAddress;
    if (fields.routable) {
      body.routableSocketAddress = fields.socketAddress;
    }
  } else if (fields.transport !== ChannelConnectionType.Adapter) {
    // ADAPTER is socketless: whatever the form still holds from another
    // transport is not an address for it, so no socket column is written.
    const address =
      fields.transport === ChannelConnectionType.Serial ? fields.serialPath : fields.socketAddress;
    if (fields.routable) {
      body.routableSocketAddress = address;
    } else {
      body.vendorSocketAddress = address;
    }
  }
  if (fields.transport === ChannelConnectionType.Serial) {
    body.baudRate = fields.baudRate;
  }
  if (fields.heartbeatMessageId) {
    body.heartbeatMessage = compositeKeyToMessageT(
      fields.heartbeatMessageId,
      undefined,
      fields.heartbeatEncoding !== EncodedMessageContent.NONE
        ? fields.heartbeatEncoding
        : undefined,
    );
  }
  body.mode = fields.channelMode;
  body.controlWriteMode = fields.controlWriteMode;
  if (fields.channelMode !== ChannelMode.SINGLE) {
    body.endpoints = fields.endpoints.map((ep) => ({
      address: ep.address,
      groupId: ep.groupId,
      priority: ep.priority,
      transportType: ep.transportType,
      baudRate: ep.baudRate,
      adapterId: ep.adapterId || null,
    }));
  }
  if (fields.opcUaEndpointUrl.trim() !== "") {
    body.opc_ua_endpoint_url = fields.opcUaEndpointUrl;
  }
  body.mtlsEnabled = fields.mtlsEnabled;
  body.mtlsSecretName = fields.mtlsSecretName || null;
  // Session protocol (telnet, SSH) and its write-only secrets, under the
  // generated ChannelT names both Channel serializers read. What goes, and
  // when it is left out so the stored session is kept, is decided in one
  // place: `sessionRequestFields`.
  if (fields.session) {
    const session = sessionRequestFields(
      fields.session,
      sessionApplies(fbsType, fields.channelMode),
    );
    body.session = session.session;
    body.sessionCredentials = session.sessionCredentials;
    body.sessionPasswordClear = session.sessionPasswordClear;
    body.sessionPrivateKeyClear = session.sessionPrivateKeyClear;
  }
  return body;
}

interface StepChannelsProps {
  editChannelId?: string;
  standalone?: boolean;
  onSaveSuccess?: () => void;
  onCancel?: () => void;
}

export default function StepChannels({
  editChannelId,
  standalone = false,
  onSaveSuccess,
  onCancel,
}: StepChannelsProps = {}) {
  const [t] = useTranslation("global");
  const { theme } = useSelector((state: RootState) => state.home);
  const {
    state: wizardState,
    setState: setWizardState,
    markStepCompleted,
    unmarkStepCompleted,
  } = useWizard();

  // ************************************************ */
  // LOCAL STATE ************************************ */
  const [channelName, setChannelName] = useState("");
  const [channelType, setChannelType] = useState<ChannelConnectionType>(ChannelConnectionType.Tcp);
  const [socketAddress, setSocketAddress] = useState("");
  const [vendorSocketAddress, setVendorSocketAddress] = useState("");
  const [serialPath, setSerialPath] = useState("");
  const [active, setActive] = useState(true);
  const [routable, setRoutable] = useState(false);
  const [heartbeatFrequency, setHeartbeatFrequency] = useState<number | null>(null);
  const [heartbeatMessageId, setHeartbeatMessageId] = useState("");
  const [heartbeatEncoding, setHeartbeatEncoding] = useState<EncodedMessageContent>(
    EncodedMessageContent.NONE,
  );
  const [baudRate, setBaudRate] = useState<number>(9600);
  const [channels, setChannels] = useState<ChannelRow[]>([]);
  const [existingLoaded, setExistingLoaded] = useState(false);
  const [editingChannelId, setEditingChannelId] = useState<string | null>(null);
  const access = useCurrentWizardStepAccess();
  const [stepError, setStepError] = useState<string | null>(null);
  // Quick enable/disable (table Switch + bulk buttons) — tracks which rows
  // have an in-flight toggle so the Switch can show a loading state and the
  // bulk buttons can be disabled/loading while any toggle (row or bulk) runs.
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());
  const [channelMode, setChannelMode] = useState<ChannelMode>(ChannelMode.SINGLE);
  const [endpoints, setEndpoints] = useState<EndpointRow[]>([]);
  // Control write routing (MERGE/MIXED). `originalControlRoutes` is the set as
  // loaded, kept so the save can diff against it rather than blindly rewriting
  // every rule on every channel edit.
  const [controlWriteMode, setControlWriteMode] = useState<ControlWriteMode>(
    ControlWriteMode.ACTIVE_ENDPOINT,
  );
  const [controlRoutes, setControlRoutes] = useState<ControlRouteRow[]>([]);
  const [originalControlRoutes, setOriginalControlRoutes] = useState<ControlRouteRow[]>([]);
  const [routableEndpoints, setRoutableEndpoints] = useState<RoutableEndpoint[]>([]);
  const [opcUaEndpointUrl, setOpcUaEndpointUrl] = useState("");
  const [mtlsEnabled, setMtlsEnabled] = useState(false);
  const [mtlsSecretName, setMtlsSecretName] = useState("");
  const [session, setSession] = useState<ChannelSessionState>(() => newChannelSession());

  // ************************************************ */
  // DRAFT PERSISTENCE ****************************** */
  // Channel creation is a long form (transport, addresses, heartbeat,
  // baud, endpoints). The operator often half-fills it, gets pulled
  // away, and returns — without persistence the form is blank and they
  // have to start over. Persist every operator-decided field so the
  // form rehydrates exactly as left.
  //
  // The `channels` list itself comes from the server via GET and is NOT
  // persisted here.
  //
  // Verification: skip the draft entirely in standalone edit mode (the
  // edge-management editor handles its own hydration via editChannelId
  // and would be confused by a wizard draft) and when editing an
  // existing channel (editingChannelId set — the editor pre-loads from
  // the server).
  //
  // Session: only `session.config` (protocol, username, options) is
  // persisted. Drafts are stored server-side, so the typed secrets in
  // `session.credentials` never go into one; a password typed before a
  // reload has to be typed again.
  useWizardStepDraft<{
    channelName: string;
    channelType: ChannelConnectionType;
    socketAddress: string;
    vendorSocketAddress: string;
    serialPath: string;
    active: boolean;
    routable: boolean;
    heartbeatFrequency: number | null;
    heartbeatMessageId: string;
    heartbeatEncoding: EncodedMessageContent;
    baudRate: number;
    channelMode: ChannelMode;
    endpoints: EndpointRow[];
    opcUaEndpointUrl: string;
    mtlsEnabled: boolean;
    mtlsSecretName: string;
    sessionConfig: SessionConfigT;
  }>({
    orgId: wizardState.organizationId,
    step: WizardStep.Channels,
    scopeId: "channels-form",
    scopeType: "step",
    draft: {
      channelName,
      channelType,
      socketAddress,
      vendorSocketAddress,
      serialPath,
      active,
      routable,
      heartbeatFrequency,
      heartbeatMessageId,
      heartbeatEncoding,
      baudRate,
      channelMode,
      endpoints,
      opcUaEndpointUrl,
      mtlsEnabled,
      mtlsSecretName,
      sessionConfig: session.config,
    },
    onRestore: (saved) => {
      if (typeof saved.channelName === "string") setChannelName(saved.channelName);
      if (
        saved.channelType === ChannelConnectionType.Tcp ||
        saved.channelType === ChannelConnectionType.Udp ||
        saved.channelType === ChannelConnectionType.Serial
      ) {
        setChannelType(saved.channelType);
      }
      if (typeof saved.socketAddress === "string") setSocketAddress(saved.socketAddress);
      if (typeof saved.vendorSocketAddress === "string") {
        setVendorSocketAddress(saved.vendorSocketAddress);
      }
      if (typeof saved.serialPath === "string") setSerialPath(saved.serialPath);
      if (typeof saved.active === "boolean") setActive(saved.active);
      if (typeof saved.routable === "boolean") setRoutable(saved.routable);
      if (saved.heartbeatFrequency === null || typeof saved.heartbeatFrequency === "number") {
        setHeartbeatFrequency(saved.heartbeatFrequency);
      }
      if (typeof saved.heartbeatMessageId === "string") {
        setHeartbeatMessageId(saved.heartbeatMessageId);
      }
      if (typeof saved.heartbeatEncoding === "number") {
        setHeartbeatEncoding(saved.heartbeatEncoding);
      }
      if (typeof saved.baudRate === "number") setBaudRate(saved.baudRate);
      if (typeof saved.channelMode === "number") setChannelMode(saved.channelMode);
      if (Array.isArray(saved.endpoints)) setEndpoints(saved.endpoints);
      if (typeof saved.opcUaEndpointUrl === "string") {
        setOpcUaEndpointUrl(saved.opcUaEndpointUrl);
      }
      if (typeof saved.mtlsEnabled === "boolean") setMtlsEnabled(saved.mtlsEnabled);
      if (typeof saved.mtlsSecretName === "string") setMtlsSecretName(saved.mtlsSecretName);
      if (saved.sessionConfig !== undefined) {
        setSession(newChannelSession(sessionConfigFrom(saved.sessionConfig)));
      }
    },
    // Skip the save when the operator is editing an existing channel —
    // that path loads from the server, and persisting it would clobber
    // the new-channel draft with one keyed to a different form purpose.
    disabled: standalone || !!editingChannelId,
  });

  // ************************************************ */
  // REFS ****************************************** */
  // Remembers the last successfully created channel's socket address so the
  // next form can be pre-filled (same subnet, user only changes last octet).
  const lastSocketAddressRef = useRef<string>("");

  // ************************************************ */
  // RTK QUERY HOOKS ******************************* */
  const [
    triggerPostChannel,
    {
      isSuccess: isSuccessPost,
      isLoading: isLoadingPost,
      isError: isErrorPost,
      error: errorPost,
      data: dataPost,
    },
  ] = usePostChannelMutation();

  const [
    triggerGetChannels,
    { isLoading: isLoadingGet, data: dataGetChannels, isSuccess: isSuccessGet },
  ] = useLazyGetChannelsByServerQuery();

  // Control write routing. Rules are their own resource, so they load and save
  // alongside the channel rather than as part of its body.
  const [triggerGetControlRoutes] = useLazyGetControlRoutesQuery();
  const [triggerGetChannelEndpoints] = useLazyGetChannelEndpointsQuery();
  const [triggerPostControlRoute] = usePostControlRouteMutation();
  const [triggerPutControlRoute] = usePutControlRouteMutation();
  const [triggerDeleteControlRoute] = useDeleteControlRouteMutation();

  /**
   * Loads a merged channel's routable endpoints and existing rules.
   *
   * Endpoints come from the server rather than the form's local rows: a rule
   * target is a foreign key, so it has to be the id the server assigned.
   * Failure is non-fatal — the rest of the channel editor still works, the
   * routing table simply has nothing to offer.
   */
  const loadControlRouting = async (channelId: string, token: string) => {
    try {
      const [epResult, routeResult] = await Promise.all([
        triggerGetChannelEndpoints({ channelId, token, page: 1, limit: 9999 }),
        triggerGetControlRoutes({ channelId, token }),
      ]);
      const epData = (epResult.data?.data ?? epResult.data ?? []) as Record<string, unknown>[];
      const routeData = (routeResult.data?.data ?? routeResult.data ?? []) as Record<
        string,
        unknown
      >[];
      setRoutableEndpoints(toRoutableEndpoints(epData));
      const rows = toControlRouteRows(routeData);
      setControlRoutes(rows);
      setOriginalControlRoutes(rows);
    } catch {
      setRoutableEndpoints([]);
      setControlRoutes([]);
      setOriginalControlRoutes([]);
    }
  };

  /**
   * Applies the edited rule set. Runs after the channel PUT so a rule can never
   * reference a channel the save rejected. Unchanged rules produce no request.
   */
  const saveControlRoutes = async (channelId: string, token: string) => {
    const { create, update, deleteIds } = diffControlRoutes(
      originalControlRoutes,
      controlRoutes,
      channelId,
    );
    await Promise.all([
      ...create.map((body) => triggerPostControlRoute({ body: { ...body }, token }).unwrap()),
      ...update.map((body) => triggerPutControlRoute({ body: { ...body }, token }).unwrap()),
      ...deleteIds.map((id) => triggerDeleteControlRoute({ id, token }).unwrap()),
    ]);
    setOriginalControlRoutes(controlRoutes);
  };

  const [
    triggerPutChannel,
    { isLoading: isLoadingPut, isError: isErrorPut, error: errorPut, isSuccess: isSuccessPut },
  ] = usePutChannelMutation();
  const [triggerGetChannel] = useLazyGetChannelQuery();
  const [triggerPatchChannelActive] = usePatchChannelActiveMutation();
  const [triggerDeleteChannel, { isLoading: isLoadingDelete }] = useDeleteChannelMutation();
  const [triggerGetBanksByServer] = useLazyGetBanksByServerQuery();
  const [triggerDeleteBank] = useDeleteBankMutation();

  // ************************************************ */
  // DERIVED STATE ********************************** */
  const isTcp = channelType === ChannelConnectionType.Tcp;
  const isUdp = channelType === ChannelConnectionType.Udp;
  const isSerial = channelType === ChannelConnectionType.Serial;
  const heartbeatSet = heartbeatFrequency !== null;
  const heartbeatMissingMessage = heartbeatSet && heartbeatMessageId === "";
  const socketAddressTyped = socketAddress.trim() !== "";
  const vendorSocketAddressTyped = vendorSocketAddress.trim() !== "";
  const socketAddressIsValid = socketAddressTyped && validateSocketAddress(socketAddress);
  const vendorAddressIsValid =
    vendorSocketAddressTyped && validateSocketAddress(vendorSocketAddress);
  // OPC UA endpoint is optional (not every channel backs an OPC UA adapter)
  // — it only blocks submission when typed but invalid. When present and
  // valid, it also stands in for the TCP socket address instead of merely
  // being optional: see `isChannelAddressingValid`.
  const opcUaEndpointTyped = opcUaEndpointUrl.trim() !== "";
  const opcUaEndpointIsValid = !opcUaEndpointTyped || isValidOpcUaEndpointUrl(opcUaEndpointUrl);
  const opcUaEndpointSatisfiesAddress = opcUaEndpointTyped && opcUaEndpointIsValid;
  const socketAddressValid = isChannelAddressingValid({
    isTcp,
    isUdp,
    socketAddressTyped,
    socketAddressIsValid,
    vendorAddressIsValid,
    opcUaEndpointSatisfiesAddress,
  });
  // The session section applies to SINGLE-mode TCP channels only; its rules
  // are LiftCloud's, checked before the save is sent.
  const sessionIssue = sessionProblem(
    session,
    sessionApplies(toFbsChannelType(channelType), channelMode),
  );

  // ************************************************ */
  // FUNCTIONS ************************************* */
  const onChangeFormValue = (newValue: string, variableName: string) => {
    if (variableName === "channelName") setChannelName(newValue);
  };

  const clearForm = () => {
    setChannelName("");
    setChannelType(ChannelConnectionType.Tcp);
    setSocketAddress(lastSocketAddressRef.current);
    setVendorSocketAddress(lastSocketAddressRef.current);
    setSerialPath("");
    setActive(true);
    setRoutable(false);
    setHeartbeatFrequency(null);
    setHeartbeatMessageId("");
    setHeartbeatEncoding(EncodedMessageContent.NONE);
    setBaudRate(9600);
    setChannelMode(ChannelMode.SINGLE);
    setControlWriteMode(ControlWriteMode.ACTIVE_ENDPOINT);
    setControlRoutes([]);
    setOriginalControlRoutes([]);
    setRoutableEndpoints([]);
    setEndpoints([]);
    setOpcUaEndpointUrl("");
    setMtlsEnabled(false);
    setMtlsSecretName("");
    setSession(newChannelSession());
  };

  const isFormValid = () => {
    const baseValid =
      channelName.trim() !== "" &&
      !!wizardState.serverId &&
      !heartbeatMissingMessage &&
      opcUaEndpointIsValid &&
      sessionIssue === null;

    if (channelMode === ChannelMode.SINGLE) {
      return baseValid && socketAddressValid && (!isSerial || serialPath.trim() !== "");
    }
    // Non-SINGLE: endpoints must be non-empty and pass validation
    return baseValid && endpoints.length > 0 && validateEndpoints(channelMode, endpoints) === null;
  };

  const getDisabledReason = (): string => {
    if (heartbeatMissingMessage)
      return "A Heartbeat Message must be selected when Heartbeat Frequency is set.";
    if (isTcp && socketAddressTyped && !socketAddressIsValid)
      return "Socket address must be IPv4:port, [IPv6]:port, or hostname:port.";
    if (isUdp && vendorSocketAddressTyped && !vendorAddressIsValid)
      return "Controller address must be IPv4:port, [IPv6]:port, or hostname:port.";
    if (isUdp && socketAddressTyped && !socketAddressIsValid)
      return "Edge address must be IPv4:port, [IPv6]:port, or hostname:port.";
    if (isSerial && serialPath.trim() === "")
      return "Device path is required for serial connections.";
    if (!opcUaEndpointIsValid) return "OPC UA Endpoint URL must be opc.tcp://host:port.";
    if (sessionIssue) return sessionIssue;
    return "";
  };

  /**
   * The vendor-facing address VTS will actually connect to, mirroring what
   * gets persisted as the channel's vendorSocketAddress (TCP: the IP:Port
   * field; UDP: the Controller IP:Port field). Serial channels have no
   * network address to derive an mTLS secret name from.
   */
  const getEffectiveVendorSocketAddress = (): string | null => {
    if (isTcp) return socketAddress;
    if (isUdp) return vendorSocketAddress;
    return null;
  };

  const channelFormFields = (): ChannelBodyFields => ({
    name: channelName,
    transport: channelType,
    active,
    routable,
    heartbeatFrequency,
    heartbeatMessageId,
    heartbeatEncoding,
    socketAddress,
    vendorSocketAddress,
    serialPath,
    baudRate,
    channelMode,
    controlWriteMode,
    endpoints,
    opcUaEndpointUrl,
    mtlsEnabled,
    mtlsSecretName,
    session,
  });

  const handleAddChannel = async () => {
    if (!isFormValid()) return;
    const token = await GETJwtToken();
    const body = buildChannelBody(
      crypto.randomUUID(),
      wizardState.serverId,
      wizardState.organizationId,
      channelFormFields(),
    );
    triggerPostChannel({ body, token });
  };

  const fetchExistingChannels = async () => {
    if (!wizardState.serverId) return;
    const token = await GETJwtToken();
    triggerGetChannels({
      serverId: wizardState.serverId,
      page: 1,
      limit: 100,
      token,
      searchAndFilterString: "",
    });
  };

  const handleDeleteChannel = async (channelId: string) => {
    const token = await GETJwtToken();
    // Track which banks were successfully deleted so state stays consistent
    // even if the cascade is partially interrupted.
    const deletedBankIds: string[] = [];
    try {
      if (wizardState.serverId && wizardState.bankIds.length > 0) {
        const bankResult = await triggerGetBanksByServer({
          idServer: wizardState.serverId,
          page: 1,
          limit: 9999,
          searchAndFilterString: "",
          token,
        }).unwrap();
        const allBanks = bankResult?.data || [];
        const dependentBanks = allBanks.filter(
          (b: any) => (formatUuidT(b.channelId) || b.channel_id) === channelId,
        );
        for (let i = 0; i < dependentBanks.length; i += 1) {
          const bankId = formatUuidT(dependentBanks[i].id) || dependentBanks[i].id;
          // eslint-disable-next-line no-await-in-loop
          await triggerDeleteBank({ id: bankId, token }).unwrap();
          deletedBankIds.push(bankId);
        }
      }
      await triggerDeleteChannel({ id: channelId, token }).unwrap();
      // All deletes succeeded — now update state atomically
      if (deletedBankIds.length > 0) {
        setWizardState((prev) => {
          const updated = structuredClone(prev);
          updated.bankIds = updated.bankIds.filter((id: string) => !deletedBankIds.includes(id));
          deletedBankIds.forEach((bid) => {
            delete updated.liftIdsByBank[bid];
            delete updated.bankNames[bid];
            delete updated.bankTypes[bid];
          });
          return updated;
        });
        // Channel-deletion cascade: banks were also wiped above, so every
        // step downstream of Banks loses its referent — through Review,
        // whose completion flag SetupWizardPage reads generically for
        // breadcrumb status and for its forward-jump guard, and through
        // Users, whose role scope grants are keyed by bank id (an empty
        // grant map means allow-all — see redux/scope/scopeSlice.ts — so a
        // role scoped only to a deleted bank silently stops being
        // restricted). The list this replaces began as the literal
        // `(4, 5, 6, 7)` before ChannelModifiers was inserted at index 5
        // and had since drifted off the end. Gapless on purpose: the guard
        // (`step <= currentStep || isStepCompleted(step) ||
        // isStepCompleted(step - 1)`) makes any step left complete inside
        // the range a one-click hop-in target from anywhere.
        unmarkStepCompleted(...wizardStepsFrom(WizardStep.Banks));
      }
      setChannels((prev) => prev.filter((c) => c.id !== channelId));
      setWizardState((prev) => ({
        ...prev,
        channelIds: prev.channelIds.filter((id: string) => id !== channelId),
      }));
      if (channels.length <= 1) {
        unmarkStepCompleted(WizardStep.Channels);
      }
      setEditingChannelId(null);
      clearForm();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[StepChannels] DELETE channel failed:", err);
      // Apply partial bank cleanup even when the channel delete failed so
      // wizard state stays in sync with what was actually deleted on the server.
      if (deletedBankIds.length > 0) {
        setWizardState((prev) => {
          const updated = structuredClone(prev);
          updated.bankIds = updated.bankIds.filter((id: string) => !deletedBankIds.includes(id));
          deletedBankIds.forEach((bid) => {
            delete updated.liftIdsByBank[bid];
            delete updated.bankNames[bid];
            delete updated.bankTypes[bid];
          });
          return updated;
        });
        // Same range as the success path above, for the same reason: those
        // banks really were deleted server-side even though the channel
        // delete then failed, so every step that referenced them is stale
        // regardless of which branch we landed in.
        unmarkStepCompleted(...wizardStepsFrom(WizardStep.Banks));
      }
      const msg = describeError(err);
      setStepError(`Failed to delete channel — ${msg}`);
    }
  };

  const handleEditChannel = async (channelId: string) => {
    const token = await GETJwtToken();
    const result = await triggerGetChannel({ channelId, token });
    const d = result.data?.data || result.data;
    if (!d) {
      setStepError("Failed to load channel for editing — please try again");
      return;
    }
    const validTransport = transportFromConnectionType(d.connectionType ?? d.connection_type);
    setChannelName(d.name || "");
    setChannelType(validTransport);
    setActive(d.active !== false);
    setRoutable(d.routable === true);
    setHeartbeatFrequency(normalizeHeartbeatFrequency(d.heartbeatFrequency));
    setHeartbeatMessageId(
      d.heartbeatMessage ? (messageTToCompositeKey(d.heartbeatMessage) ?? "") : "",
    );
    setHeartbeatEncoding(d.heartbeatMessage?.contentType ?? EncodedMessageContent.NONE);
    setBaudRate(d.baudRate ?? 9600);
    const addrs = resolveEditAddresses(d, validTransport);
    setSocketAddress(addrs.socketAddress);
    setVendorSocketAddress(addrs.vendorSocketAddress);
    setSerialPath(addrs.serialPath);
    // Populate channelMode and endpoints (back-compat: default to SINGLE/[] if absent)
    setChannelMode((d.mode as ChannelMode) ?? ChannelMode.SINGLE);
    setEndpoints(Array.isArray(d.endpoints) ? (d.endpoints as EndpointRow[]) : []);
    setOpcUaEndpointUrl(resolveChannelOpcUaEndpointUrl(d));
    setMtlsEnabled(d.mtlsEnabled === true || d.mtls_enabled === true);
    setMtlsSecretName(String(d.mtlsSecretName || d.mtls_secret_name || ""));
    // Settings and stored-secret flags only: no response carries a secret, so
    // the password and key inputs start empty.
    setSession(sessionStateFromChannel(d));
    setControlWriteMode(
      (d.controlWriteMode as ControlWriteMode) ??
        (d.control_write_mode as ControlWriteMode) ??
        ControlWriteMode.ACTIVE_ENDPOINT,
    );
    const loadedMode = (d.mode as ChannelMode) ?? ChannelMode.SINGLE;
    if (loadedMode === ChannelMode.MERGE || loadedMode === ChannelMode.MIXED) {
      await loadControlRouting(channelId, token);
    } else {
      setRoutableEndpoints([]);
      setControlRoutes([]);
      setOriginalControlRoutes([]);
    }
    setEditingChannelId(channelId);
  };

  const handleUpdateChannel = async () => {
    if (!isFormValid() || !editingChannelId) return;
    const token = await GETJwtToken();
    const body = buildChannelBody(
      editingChannelId,
      wizardState.serverId,
      wizardState.organizationId,
      channelFormFields(),
    );
    // Capture the pre-update channel snapshot so we can diff against the
    // post-update state and only unmark downstream wizard steps that
    // genuinely depend on what changed. Field engineers most often edit
    // network fields (IP, port, baudrate) which have no downstream
    // dependency — the previous unconditional unmarkStepCompleted(4,5,6,7)
    // forced a needless re-walk through Banks/Lifts/Debounce/BACnet just
    // to clear a typo'd address. See utils/wizardInvalidation.ts for the
    // dependency rules.
    const oldChannel = channels.find((c) => c.id === editingChannelId);
    try {
      await triggerPutChannel({ body, token }).unwrap();
      // After the channel, never before: a rule names a channel_id, so writing
      // rules for a save the server rejected would leave orphans behind.
      if (channelMode === ChannelMode.MERGE || channelMode === ChannelMode.MIXED) {
        await saveControlRoutes(editingChannelId, token);
      }
      const addr = isSerial ? serialPath : socketAddress;
      const updatedChannels = channels.map((c) =>
        c.id === editingChannelId
          ? {
              ...c,
              name: channelName,
              type: channelType,
              active,
              routable,
              socketAddress: addr,
              opcUaEndpointUrl,
              heartbeatFrequency,
              mtlsEnabled,
              mtlsSecretName,
            }
          : c,
      );
      setChannels(updatedChannels);
      if (allChannelsValid(updatedChannels)) {
        markStepCompleted(3);
      } else {
        unmarkStepCompleted(3);
      }
      setEditingChannelId(null);
      clearForm();
      if (standalone && onSaveSuccess) {
        onSaveSuccess();
        return;
      }
      const newChannel = updatedChannels.find((c) => c.id === editingChannelId);
      const stepsToInvalidate = computeInvalidatedSteps(
        oldChannel as Record<string, unknown> | undefined,
        newChannel as Record<string, unknown> | undefined,
        CHANNEL_FIELD_RULES,
      );
      if (stepsToInvalidate.length > 0) {
        unmarkStepCompleted(...stepsToInvalidate);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[StepChannels] PUT channel update failed:", err);
      const msg = describeError(err);
      setStepError(`Failed to update channel — ${msg}`);
    }
  };

  const cancelEdit = () => {
    setEditingChannelId(null);
    clearForm();
    if (standalone && onCancel) {
      onCancel();
    }
  };

  const markToggling = (ids: string[]) => {
    setTogglingIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const clearToggling = (ids: string[]) => {
    setTogglingIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));
      return next;
    });
  };

  /**
   * PATCHes just the `active` column via the channels active-toggle endpoint
   * — a true partial update, unlike PUT (see `buildChannelBody`'s doc
   * comment). Used by both the per-row Switch and the bulk Enable All /
   * Disable All buttons. Returns the applied `{id, active}` pair on success
   * (for the caller to fold into `channels` state) or `null` on failure.
   */
  const toggleChannelActive = async (
    channelId: string,
    nextActive: boolean,
  ): Promise<{ id: string; active: boolean } | null> => {
    const token = await GETJwtToken();
    try {
      await triggerPatchChannelActive({ id: channelId, active: nextActive, token }).unwrap();
      return { id: channelId, active: nextActive };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[StepChannels] Quick toggle failed:", err);
      const msg = describeError(err);
      setStepError(`Failed to update channel — ${msg}`);
      return null;
    }
  };

  const handleQuickToggleActive = async (channelId: string, nextActive: boolean) => {
    markToggling([channelId]);
    try {
      const applied = await toggleChannelActive(channelId, nextActive);
      if (applied) {
        setChannels((prev) =>
          prev.map((c) => (c.id === applied.id ? { ...c, active: applied.active } : c)),
        );
      }
    } finally {
      clearToggling([channelId]);
    }
  };

  const handleBulkSetActive = async (nextActive: boolean) => {
    const targetIds = channels.filter((c) => c.active !== nextActive).map((c) => c.id);
    if (targetIds.length === 0) return;
    markToggling(targetIds);
    try {
      const results = await Promise.all(targetIds.map((id) => toggleChannelActive(id, nextActive)));
      const applied = new Map(
        results
          .filter((r): r is { id: string; active: boolean } => !!r)
          .map((r) => [r.id, r.active]),
      );
      if (applied.size > 0) {
        setChannels((prev) =>
          prev.map((c) => (applied.has(c.id) ? { ...c, active: applied.get(c.id) as boolean } : c)),
        );
      }
    } finally {
      clearToggling(targetIds);
    }
  };

  const getAddButtonLabel = () => {
    if (editingChannelId) return "Update Channel";
    if (channels.length > 0) return "+ Add Another Channel";
    return "+ Add Channel";
  };

  // ************************************************ */
  // USE EFFECTS *********************************** */
  useEffect(() => {
    if (wizardState.serverId) {
      fetchExistingChannels();
    }
  }, [wizardState.serverId]);

  useEffect(() => {
    if (!isSuccessGet || !dataGetChannels || existingLoaded) return;
    const items = dataGetChannels.data || [];
    if (items.length > 0) {
      const existingRows: ChannelRow[] = items.map((ch: ChannelInterface) => {
        const chId = formatUuidT(ch.id) || String(ch.id ?? "");
        return {
          key: chId,
          id: chId,
          name: String(ch.name || "") || "Default Channel",
          type: transportFromConnectionType(ch.connectionType),
          active: ch.active !== false,
          routable: ch.routable === true,
          socketAddress: resolveChannelSocketAddress(ch),
          opcUaEndpointUrl: resolveChannelOpcUaEndpointUrl(ch),
          heartbeatFrequency: normalizeHeartbeatFrequency(ch.heartbeatFrequency),
          mtlsEnabled: ch.mtlsEnabled === true,
          mtlsSecretName: String(ch.mtlsSecretName || ""),
        };
      });
      setChannels(existingRows);
      const existingIds = items.map(
        (ch: ChannelInterface) => formatUuidT(ch.id) || String(ch.id ?? ""),
      );
      setWizardState((prev) => {
        const mergedIds = structuredClone(prev.channelIds);
        existingIds.forEach((id: string) => {
          if (!mergedIds.includes(id)) {
            mergedIds.push(id);
          }
        });
        return { ...prev, channelIds: mergedIds };
      });
      if (allChannelsValid(existingRows)) {
        markStepCompleted(3);
      } else {
        unmarkStepCompleted(3);
      }
    }
    setExistingLoaded(true);
  }, [isSuccessGet, dataGetChannels]);

  useEffect(() => {
    if (isSuccessPost && dataPost) {
      const channelData = dataPost.data || dataPost;
      const channelId = formatUuidT(channelData.id) || channelData.id;
      if (!channelId) return;
      const addr = isSerial ? serialPath : socketAddress;
      const newRow: ChannelRow = {
        key: channelId,
        id: channelId,
        name: channelName,
        type: channelType,
        active,
        routable,
        socketAddress: addr,
        opcUaEndpointUrl,
        heartbeatFrequency,
        mtlsEnabled,
        mtlsSecretName,
      };
      const updatedChannels = [...channels, newRow];
      setChannels(updatedChannels);
      if (allChannelsValid(updatedChannels)) {
        markStepCompleted(3);
      }
      setWizardState((prev) => ({
        ...prev,
        channelIds: [...prev.channelIds, channelId],
      }));
      lastSocketAddressRef.current = socketAddress || vendorSocketAddress || serialPath;
      clearForm();
    }
  }, [isSuccessPost, dataPost]);

  useEffect(() => {
    if (!editChannelId) return;
    const initEdit = async () => {
      const token = await GETJwtToken();
      const result = await triggerGetChannel({ channelId: editChannelId, token });
      const d = result.data?.data || result.data;
      if (d) {
        const serverId =
          typeof d.serverId === "object"
            ? formatUuidT(d.serverId) || ""
            : d.serverId || d.server_id || "";
        const orgId =
          typeof d.organizationId === "object"
            ? formatUuidT(d.organizationId) || ""
            : d.organizationId || d.organization_id || "";
        setWizardState((prev) => ({ ...prev, serverId, organizationId: orgId }));
      }
    };
    initEdit();
  }, [editChannelId]);

  useEffect(() => {
    if (editChannelId && existingLoaded && channels.length > 0) {
      handleEditChannel(editChannelId);
    }
  }, [editChannelId, existingLoaded, channels.length]);

  // ************************************************ */
  // TABLE COLUMNS ********************************* */
  const channelColumns = [
    {
      title: t("general.name"),
      dataIndex: "name",
      key: "name",
      render: (name: string, record: ChannelRow) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          {name}
          <WizardEntityLink entityType="channel" entityId={record.id} />
        </span>
      ),
    },
    {
      title: "Transport",
      dataIndex: "type",
      key: "type",
      render: (val: ChannelConnectionType | number) =>
        typeof val === "number" ? (CHANNEL_TYPE_NAMES[val] ?? ChannelConnectionType.Tcp) : val,
    },
    {
      title: "IP:Port",
      dataIndex: "socketAddress",
      key: "socketAddress",
      render: (addr: string, record: ChannelRow) => {
        if (record.type === ChannelConnectionType.Serial) {
          return <span style={{ color: "#888" }}>N/A</span>;
        }
        if (record.type === ChannelConnectionType.Adapter) {
          // Socketless by definition — see channelHasValidAddress.
          return (
            <span style={{ color: "#888" }}>
              {record.opcUaEndpointUrl ? "via endpoint" : "N/A"}
            </span>
          );
        }
        if (!addr) {
          // A TCP channel addressed purely by its OPC UA endpoint is complete,
          // not missing an address — showing "Not set" in red here is what made
          // the unmarked Channels step impossible to diagnose. The endpoint
          // itself is in the next column.
          if (record.type === ChannelConnectionType.Tcp && record.opcUaEndpointUrl) {
            return <span style={{ color: "#888" }}>via endpoint</span>;
          }
          return <span style={{ color: "#ff4d4f" }}>Not set</span>;
        }
        return addr;
      },
    },
    {
      // OPC UA addresses by URL rather than by host:port, and the endpoint is
      // the only thing LiftCloud reads for such a channel — so the operator has
      // to be able to see which channel carries one.
      title: "OPC UA Endpoint",
      dataIndex: "opcUaEndpointUrl",
      key: "opcUaEndpointUrl",
      render: (url: string | undefined) => {
        if (!url) return <span style={{ color: "#888" }}>—</span>;
        if (!isValidOpcUaEndpointUrl(url)) {
          return (
            <span style={{ color: "#ff4d4f" }} title="Not a valid opc.tcp://host:port URL">
              {url}
            </span>
          );
        }
        return <span title={url}>{url}</span>;
      },
    },
    {
      title: t("general.active"),
      dataIndex: "active",
      key: "active",
      render: (val: boolean, record: ChannelRow) => (
        <WizardOpGate op="update">
          <Switch
            size="small"
            checked={val}
            loading={togglingIds.has(record.id)}
            onChange={(checked) => handleQuickToggleActive(record.id, checked)}
            data-testid={`channel-active-switch-${record.id}`}
          />
        </WizardOpGate>
      ),
    },
    {
      title: "Heartbeat (ms)",
      dataIndex: "heartbeatFrequency",
      key: "heartbeatFrequency",
      render: (val: number | null) =>
        val != null && val > 0 ? val : <span style={{ color: "#888" }}>None</span>,
    },
    {
      title: "",
      key: "actions",
      width: 140,
      render: (_: unknown, record: ChannelRow) => (
        <div style={{ display: "flex", gap: 4 }}>
          <WizardOpGate op="update">
            <Button type="link" size="small" onClick={() => handleEditChannel(record.id)}>
              Edit
            </Button>
          </WizardOpGate>
          <WizardOpGate op="delete">
            <Popconfirm
              title="Delete this channel?"
              onConfirm={() => handleDeleteChannel(record.id)}
              okText="Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Button type="link" danger size="small" loading={isLoadingDelete}>
                Delete
              </Button>
            </Popconfirm>
          </WizardOpGate>
        </div>
      ),
    },
  ];

  // ************************************************ */
  // COMPONENT ************************************* */
  const disabledReason = getDisabledReason();

  return (
    <Spin spinning={isLoadingPost || isLoadingPut || isLoadingGet || isLoadingDelete}>
      <div style={{ padding: "16px" }}>
        <DrawerRowDivider icon={faListCheck} title="Existing Channels" />
        <div className={`drawer__box__${theme}`} style={{ padding: "16px" }}>
          {!standalone && channels.length > 0 && (
            <>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 8 }}>
                <WizardOpGate op="update">
                  <Button
                    size="small"
                    data-testid="enable-all-channels-btn"
                    loading={togglingIds.size > 0}
                    disabled={togglingIds.size > 0 || channels.every((c) => c.active)}
                    onClick={() => handleBulkSetActive(true)}
                  >
                    Enable All
                  </Button>
                  <Button
                    size="small"
                    data-testid="disable-all-channels-btn"
                    loading={togglingIds.size > 0}
                    disabled={togglingIds.size > 0 || channels.every((c) => !c.active)}
                    onClick={() => handleBulkSetActive(false)}
                  >
                    Disable All
                  </Button>
                </WizardOpGate>
              </div>
              <Table
                dataSource={channels}
                columns={channelColumns}
                pagination={false}
                size="small"
                locale={{ emptyText: "No channels found" }}
                rowKey="id"
                onRow={() =>
                  ({ "data-testid": "channel-list-row" }) as React.HTMLAttributes<HTMLElement>
                }
              />
            </>
          )}
        </div>
        <br />
        <DrawerRowDivider icon={faEthernet} title="Channel Configuration" />
        <WizardReadOnlyId label="Channel ID" value={editingChannelId} />
        <div className={`drawer__box__${theme}`} style={{ padding: "16px" }}>
          {stepError && (
            <Alert
              type="error"
              showIcon
              closable
              message={stepError}
              onClose={() => setStepError(null)}
              style={{ marginBottom: 16 }}
            />
          )}
          {!standalone && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              data-testid="channels-intro"
              message={channelsIntroMessage(channels.length)}
            />
          )}
          <div className="container">
            {/* Name + Transport Mode */}
            <div className="row">
              <div className="col-12 col-md-4 mt-3">
                <FormInputText
                  inputValue={channelName}
                  inputLabelName={t("general.name")}
                  inputOnChange={onChangeFormValue}
                  inputVariableName="channelName"
                  isRequired
                  disabledInput={false}
                  formularyType={FormularyType.WIZARD}
                />
              </div>
              <div className="col-12 col-md-4 mt-3">
                <div>
                  <span className="generalStyles__inputFlexRequired">*</span>
                  <span className="generalStyles__inputLabel">Transport Mode</span>
                </div>
                <Select
                  style={{ width: "100%", marginTop: 4 }}
                  value={channelType}
                  onChange={(val: ChannelConnectionType) => {
                    setChannelType(val);
                    setSocketAddress("");
                    setVendorSocketAddress("");
                    setSerialPath("");
                  }}
                  size="large"
                >
                  {TRANSPORT_MODES.map((opt) => (
                    <Select.Option key={opt} value={opt}>
                      {TRANSPORT_MODE_LABELS[opt]}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </div>

            {/* Channel Mode — new in D1 */}
            <div className="row">
              <div className="col-12 col-md-4 mt-3">
                <div>
                  <span className="generalStyles__inputFlexRequired">*</span>
                  <span className="generalStyles__inputLabel">Channel Mode</span>
                </div>
                <Select
                  data-testid="channel-mode-select"
                  style={{ width: "100%", marginTop: 4 }}
                  value={channelMode}
                  size="large"
                  onChange={(val: ChannelMode) => {
                    setChannelMode(val);
                    setEndpoints([]);
                  }}
                  options={CHANNEL_MODE_OPTIONS}
                />
              </div>
              <div className="col-12 col-md-4 mt-3">
                <div>
                  <span className="generalStyles__inputFlexRequired">*</span>
                  <span className="generalStyles__inputLabel">Active</span>
                </div>
                <Switch
                  checked={active}
                  onChange={(checked) => setActive(checked)}
                  style={{ marginTop: "10px" }}
                />
              </div>
              <div className="col-12 col-md-4 mt-3">
                <div>
                  <span className="generalStyles__inputLabel">
                    {t("edgeManagement.channels.channelType")}
                  </span>
                </div>
                <Select
                  data-testid="channel-type-select"
                  style={{ width: "100%", marginTop: 4 }}
                  value={channelTypeFromRoutable(routable)}
                  size="large"
                  onChange={(val: string) => setRoutable(routableFromChannelType(val))}
                  options={[
                    { label: t("edgeManagement.channels.single"), value: CHANNEL_TYPE_SINGLE },
                    { label: t("edgeManagement.channels.routable"), value: CHANNEL_TYPE_ROUTABLE },
                  ]}
                />
              </div>
            </div>

            {/* TCP: single socket address */}
            {channelMode === ChannelMode.SINGLE && isTcp && (
              <div className="row">
                <div className="col-12 col-md-12 mt-3">
                  <Tooltip title="The vendor controller address that VTS connects to">
                    {!opcUaEndpointSatisfiesAddress && (
                      <span className="generalStyles__inputFlexRequired">*</span>
                    )}
                    <span className="generalStyles__inputLabel">IP:Port</span>
                  </Tooltip>
                  <input
                    className="ant-input ant-input-middle"
                    style={{
                      width: "100%",
                      marginTop: 4,
                      border:
                        socketAddressTyped && !socketAddressIsValid
                          ? "1px solid #ff4d4f"
                          : undefined,
                    }}
                    value={socketAddress}
                    placeholder="192.168.1.100:4840"
                    onChange={(e) => setSocketAddress(e.target.value)}
                  />
                  {socketAddressTyped && !socketAddressIsValid && (
                    <span style={{ color: "#ff4d4f", fontSize: 12 }}>
                      Must be IPv4:port, [IPv6]:port, or hostname:port (port 1–65535)
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Session protocol (telnet / SSH) — the editor renders only for a
                SINGLE-mode TCP channel, by the same sessionApplies rule the
                request body uses. */}
            <ChannelSessionEditor
              connectionType={toFbsChannelType(channelType)}
              mode={channelMode}
              value={session}
              onChange={setSession}
            />

            {/* UDP: target (controller) + source (edge) addresses */}
            {channelMode === ChannelMode.SINGLE && isUdp && (
              <>
                <div className="row">
                  <div className="col-12 col-md-6 mt-3">
                    <Tooltip title="The vendor controller's IP address and port that receives UDP packets">
                      <span className="generalStyles__inputFlexRequired">*</span>
                      <span className="generalStyles__inputLabel">Controller IP:Port </span>
                    </Tooltip>
                    <input
                      className="ant-input ant-input-middle"
                      style={{
                        width: "100%",
                        marginTop: 4,
                        border:
                          vendorSocketAddressTyped && !vendorAddressIsValid
                            ? "1px solid #ff4d4f"
                            : undefined,
                      }}
                      value={vendorSocketAddress}
                      placeholder="10.0.0.100:47808"
                      onChange={(e) => setVendorSocketAddress(e.target.value)}
                    />
                    {vendorSocketAddressTyped && !vendorAddressIsValid && (
                      <span style={{ color: "#ff4d4f", fontSize: 12 }}>
                        Must be IPv4:port, [IPv6]:port, or hostname:port (port 1–65535)
                      </span>
                    )}
                  </div>
                  <div className="col-12 col-md-6 mt-3">
                    <Tooltip title="The edge controller's local IP address and port for sending/receiving UDP traffic">
                      <span className="generalStyles__inputFlexRequired">*</span>
                      <span className="generalStyles__inputLabel">Edge Controller IP:Port </span>
                    </Tooltip>
                    <input
                      className="ant-input ant-input-middle"
                      style={{
                        width: "100%",
                        marginTop: 4,
                        border:
                          socketAddressTyped && !socketAddressIsValid
                            ? "1px solid #ff4d4f"
                            : undefined,
                      }}
                      value={socketAddress}
                      placeholder="10.0.0.1:47809"
                      onChange={(e) => setSocketAddress(e.target.value)}
                    />
                    {socketAddressTyped && !socketAddressIsValid && (
                      <span style={{ color: "#ff4d4f", fontSize: 12 }}>
                        Must be IPv4:port, [IPv6]:port, or hostname:port (port 1–65535)
                      </span>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* SERIAL: device path + baud rate */}
            {channelMode === ChannelMode.SINGLE && isSerial && (
              <div className="row">
                <div className="col-12 col-md-6 mt-3">
                  <Tooltip title="The serial device path on the edge controller">
                    <div>
                      <span className="generalStyles__inputFlexRequired">*</span>
                      <span className="generalStyles__inputLabel">Device Path</span>
                    </div>
                  </Tooltip>
                  <input
                    className="ant-input ant-input-middle"
                    style={{ width: "100%", marginTop: 4 }}
                    value={serialPath}
                    placeholder="/dev/ttyUSB0"
                    onChange={(e) => setSerialPath(e.target.value)}
                  />
                </div>
                <div className="col-12 col-md-6 mt-3">
                  <div>
                    <span className="generalStyles__inputFlexRequired">*</span>
                    <span className="generalStyles__inputLabel">Baud Rate</span>
                  </div>
                  <InputNumber
                    style={{ width: "100%", marginTop: 4 }}
                    min={300}
                    max={115200}
                    value={baudRate}
                    onChange={(val) => setBaudRate(val || 9600)}
                  />
                </div>
              </div>
            )}
            {/* Non-SINGLE mode — endpoint table replaces address inputs */}
            {channelMode !== ChannelMode.SINGLE && (
              <div className="row">
                <div className="col-12 mt-3">
                  <EndpointTable
                    mode={channelMode}
                    rows={endpoints}
                    onChange={setEndpoints}
                    channelTransportType={toFbsChannelType(channelType)}
                  />
                </div>
              </div>
            )}

            {/* Control write routing — MERGE/MIXED only, and only once the
                channel exists. A rule targets an endpoint by UUID and a channel
                being created has no endpoint UUIDs yet; the server assigns them
                on save. Offering the editor before then would mean minting ids
                the server will not honour, which is exactly how the pin matrix
                ended up carrying ids nothing could resolve. */}
            {(channelMode === ChannelMode.MERGE || channelMode === ChannelMode.MIXED) && (
              <div className="row">
                <div className="col-12 mt-3">
                  {editingChannelId === null ? (
                    <Alert
                      type="info"
                      showIcon
                      data-testid="control-route-after-save-hint"
                      message="Control write routing is configured after the channel is saved"
                      description="Rules point at specific endpoints by id, and this channel's endpoints do not exist yet. Save the channel, then reopen it here to choose which endpoint each command is written to."
                    />
                  ) : (
                    <ControlRouteTable
                      mode={channelMode}
                      writeMode={controlWriteMode}
                      onWriteModeChange={setControlWriteMode}
                      rows={controlRoutes}
                      onChange={setControlRoutes}
                      endpoints={routableEndpoints}
                    />
                  )}
                </div>
              </div>
            )}

            {/* OPC UA Endpoint URL — only meaningful for channels backing an
                OPC UA adapter, but shown for every channel since the wizard
                does not know the adapter's protocol until the Bank step. */}
            <div className="row">
              <div className="col-12 col-md-6 mt-3">
                <Tooltip title="The OPC UA secure-channel endpoint VTS connects to. This is the whole address of an ADAPTER channel carrying an OPC UA adapter. Leave blank for non-OPC UA channels.">
                  <span className="generalStyles__inputLabel">OPC UA Endpoint URL</span>
                </Tooltip>
                <input
                  className="ant-input ant-input-middle"
                  style={{
                    width: "100%",
                    marginTop: 4,
                    border:
                      opcUaEndpointTyped && !opcUaEndpointIsValid ? "1px solid #ff4d4f" : undefined,
                  }}
                  value={opcUaEndpointUrl}
                  placeholder="opc.tcp://10.0.0.5:4840"
                  onChange={(e) => setOpcUaEndpointUrl(e.target.value)}
                />
                {opcUaEndpointTyped && !opcUaEndpointIsValid && (
                  <span style={{ color: "#ff4d4f", fontSize: 12 }}>
                    Must be opc.tcp://host:port
                  </span>
                )}
              </div>
            </div>

            {/* mTLS Client Certificate — Secrets Manager reference only,
                the browser never handles certificate bytes. */}
            <div className="row">
              <div className="col-12 mt-3">
                <ChannelMtlsEditor
                  enabled={mtlsEnabled}
                  secretName={mtlsSecretName}
                  onChangeEnabled={(val) => {
                    setMtlsEnabled(val);
                    if (!val) setMtlsSecretName("");
                  }}
                  onChangeSecretName={setMtlsSecretName}
                  vendorSocketAddress={getEffectiveVendorSocketAddress()}
                  channelId={editingChannelId}
                />
              </div>
            </div>

            {/* Heartbeat Frequency */}
            <div className="row">
              <div className="col-12 mt-3">
                <div>
                  <span className="generalStyles__inputFlexRequired">*</span>
                  <span className="generalStyles__inputLabel">Heartbeat Frequency (ms)</span>
                </div>
                <InputNumber
                  style={{ width: "100%", marginTop: 4 }}
                  min={100}
                  max={60000}
                  step={100}
                  value={heartbeatFrequency}
                  placeholder="Not set"
                  onChange={(val) => {
                    setHeartbeatFrequency(val);
                    if (!val) {
                      setHeartbeatMessageId("");
                      setHeartbeatEncoding(EncodedMessageContent.NONE);
                    }
                  }}
                  size="large"
                />
              </div>
            </div>

            {/* Heartbeat Message — shown only when heartbeat frequency is set */}
            {heartbeatSet && (
              <div className="row">
                <div className="col-12 mt-3">
                  <Alert
                    type="warning"
                    showIcon
                    message="Heartbeat requires a mapped LN2 message"
                    style={{ marginBottom: 8 }}
                  />
                </div>
                <div className="col-12 col-md-4 mt-3">
                  <div>
                    <span className="generalStyles__inputLabel">Encoding Type</span>
                  </div>
                  <Select
                    style={{ width: "100%", marginTop: 4 }}
                    size="large"
                    value={heartbeatEncoding || undefined}
                    placeholder="Default for category"
                    onChange={(val: EncodedMessageContent | undefined) => {
                      setHeartbeatEncoding(val ?? EncodedMessageContent.NONE);
                    }}
                    options={HEARTBEAT_ENCODING_OPTIONS}
                    allowClear
                  />
                </div>
                <div className="col-12 col-md-8 mt-3">
                  <div>
                    <span className="generalStyles__inputFlexRequired">*</span>
                    <span className="generalStyles__inputLabel">Heartbeat LN2 Message</span>
                  </div>
                  <div style={{ marginTop: 4 }}>
                    <SelectLn2Message
                      value={heartbeatMessageId}
                      onChange={(key: string) => setHeartbeatMessageId(key)}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        {/* ADD/UPDATE BUTTON */}
        <Divider />
        <div className={`drawer__box__${theme}`}>
          <div className="container">
            <div className="row">
              <div className="generalStyles__flexCenter">
                <div className="generalStyles__mrFix">
                  {!standalone && (
                    <WizardExportButton stage={3} orgId={wizardState.organizationId} />
                  )}
                </div>
                <div className="generalStyles__mrFix">
                  {standalone && onCancel && (
                    <Button onClick={() => onCancel()} size="large">
                      Cancel
                    </Button>
                  )}
                </div>
                <div className="generalStyles__mrFix">
                  {!standalone && editingChannelId && (
                    <Button onClick={cancelEdit} size="large">
                      Cancel Edit
                    </Button>
                  )}
                </div>
                <div className="generalStyles__mrFix">
                  <Tooltip title={disabledReason} placement="topRight">
                    <span>
                      <Button
                        data-testid="save-channel-btn"
                        type="primary"
                        onClick={editingChannelId ? handleUpdateChannel : handleAddChannel}
                        disabled={
                          !isFormValid() || (editingChannelId ? !access.update : !access.create)
                        }
                        loading={isLoadingPost || isLoadingPut}
                        size="large"
                      >
                        {getAddButtonLabel()}
                      </Button>
                    </span>
                  </Tooltip>
                </div>
              </div>
            </div>
          </div>
        </div>

        <GlobalAlert2
          isError={isErrorPost}
          isSuccess={isSuccessPost}
          requestType="POST"
          error={errorPost}
          name="Channel"
        />
        <GlobalAlert2
          isError={isErrorPut}
          isSuccess={isSuccessPut}
          requestType="PUT"
          error={errorPut}
          name="Channel"
        />
      </div>
    </Spin>
  );
}
